import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, X, Play } from "lucide-react";
import { api, errMessage } from "../../lib/api";
import { mediaSrc } from "../../lib/media";

const FILTERS = [
  { id: "all", label: "Tous" },
  { id: "photo", label: "Photos" },
  { id: "video", label: "Vidéos" },
  { id: "published", label: "Publiés" },
];

const Thumb = ({ item, className = "" }) => {
  const src = item.poster_url || item.thumbnail_url || (item.type === "photo" ? item.url : null);
  if (src) return <img src={mediaSrc(src)} alt="" loading="lazy" className={`object-cover ${className}`} />;
  return (
    <div className={`grid place-items-center bg-gradient-to-br from-[#141414] to-[#050505] text-neutral-500 ${className}`}>
      <Play size={18} />
    </div>
  );
};

export default function AdminPartnerGallery({ partner, onClose, onSaved }) {
  const [media, setMedia] = useState(null);
  const [selected, setSelected] = useState(partner.gallery_media_ids || []);
  const [filter, setFilter] = useState("all");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/media").then(({ data }) => setMedia(data)).catch((e) => toast.error(errMessage(e)));
  }, []);

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const move = (index, delta) =>
    setSelected((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/partners/${partner.id}/gallery`, { media_ids: selected });
      toast.success(`Galerie enregistrée (${selected.length} média(s))`);
      onSaved?.(selected);
      onClose();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const list = (media || []).filter((m) =>
    filter === "all" ? true : filter === "published" ? m.publication_status === "published" : m.type === filter
  );
  const byId = Object.fromEntries((media || []).map((m) => [m.id, m]));

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="partner-gallery-modal">
      <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-5xl my-8">
        <div className="flex items-start justify-between gap-4 p-6 border-b border-white/10">
          <div>
            <div className="vb-label">Galerie partenaire</div>
            <h2 className="vb-display text-2xl mt-2">{partner.business_name}</h2>
            <p className="mt-2 text-xs text-neutral-500 max-w-xl">
              Réutilisez les médias déjà téléversés. Seuls les médias publiés s'affichent
              publiquement ; l'association n'accorde aucun consentement.
            </p>
          </div>
          <button onClick={onClose} data-testid="partner-gallery-close" className="text-neutral-500 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Selection order */}
          <div>
            <div className="vb-label mb-3">Sélection ordonnée ({selected.length})</div>
            {selected.length === 0 ? (
              <div className="border border-dashed border-white/10 p-6 text-xs text-neutral-500" data-testid="partner-gallery-selection-empty">
                Aucun média sélectionné.
              </div>
            ) : (
              <div className="space-y-2" data-testid="partner-gallery-selection">
                {selected.map((id, i) => {
                  const m = byId[id];
                  return (
                    <div key={id} className="flex items-center gap-3 border border-white/10 p-2" data-testid={`partner-gallery-selected-${id}`}>
                      <span className="w-6 text-center text-xs text-neutral-500">{i + 1}</span>
                      <Thumb item={m || {}} className="h-12 w-12 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs truncate">{m?.caption || m?.original_filename || id}</div>
                        <div className="text-[11px] text-neutral-500">
                          {m?.type === "video" ? "Vidéo" : "Photo"} ·{" "}
                          {m?.publication_status === "published" ? "Publié" : "Brouillon"}
                        </div>
                      </div>
                      <button type="button" onClick={() => move(i, -1)} data-testid={`partner-gallery-up-${id}`} className="p-1.5 text-neutral-400 hover:text-white">
                        <ArrowUp size={14} />
                      </button>
                      <button type="button" onClick={() => move(i, 1)} data-testid={`partner-gallery-down-${id}`} className="p-1.5 text-neutral-400 hover:text-white">
                        <ArrowDown size={14} />
                      </button>
                      <button type="button" onClick={() => toggle(id)} data-testid={`partner-gallery-remove-${id}`} className="p-1.5 text-[#FF3B30] hover:opacity-70">
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Picker */}
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="vb-label">Médias disponibles</div>
              <div className="flex flex-wrap gap-2 ml-auto">
                {FILTERS.map((f) => (
                  <button key={f.id} className="vb-chip !py-1 !text-xs" data-active={filter === f.id} data-testid={`partner-gallery-filter-${f.id}`} onClick={() => setFilter(f.id)}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            {media === null ? (
              <div className="text-xs text-neutral-500">Chargement…</div>
            ) : list.length === 0 ? (
              <div className="border border-dashed border-white/10 p-6 text-xs text-neutral-500" data-testid="partner-gallery-picker-empty">
                Aucun média disponible pour ce filtre.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="partner-gallery-picker">
                {list.map((m) => {
                  const on = selected.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => toggle(m.id)}
                      data-testid={`partner-gallery-pick-${m.id}`}
                      data-selected={on}
                      className={`group relative text-left border transition-colors ${on ? "border-[#FF2E63]" : "border-white/10 hover:border-white/30"}`}
                    >
                      <Thumb item={m} className="aspect-square w-full" />
                      <div className="p-2">
                        <div className="text-[11px] truncate">{m.caption || m.original_filename || m.id.slice(0, 8)}</div>
                        <div className="text-[10px] text-neutral-500">
                          {m.type === "video" ? "Vidéo" : "Photo"} ·{" "}
                          {m.publication_status === "published" ? "Publié" : "Brouillon"}
                        </div>
                      </div>
                      {on && (
                        <span className="absolute top-2 right-2 bg-[#FF2E63] text-white text-[10px] px-2 py-0.5">
                          {selected.indexOf(m.id) + 1}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 flex flex-wrap gap-3 p-6 border-t border-white/10 bg-[#0a0a0a]">
          <button onClick={save} disabled={saving} className="vb-btn vb-btn-primary !py-2 disabled:opacity-40" data-testid="partner-gallery-save">
            {saving ? "Enregistrement…" : "Enregistrer la galerie"}
          </button>
          <button onClick={onClose} className="vb-btn vb-btn-ghost !py-2" data-testid="partner-gallery-cancel">
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
}
